import { useCallback, useEffect, useRef, useState } from "react";
import PortalDropdown from "./whatsappChat/PortalDropdown";
import CompanyService from "../../services/CompanyService";
import { TranslationService } from "../../services/TranslationService";
import ActivityService from "../../services/ActivityService";
import { formatDateTimeDigits } from "../../utils/FormatUtils";
import { restTimezone } from "../../utils/ParseUtils";

interface WAViewUser {
  Date: string;
  UserId: number;
  UserFullname: string;
}

export const PanelWhatsAppTitle = ({
  name,
  phone,
  messageId,
  onSelectTemplate,
  registerExternalController
}: {
  name?: string;
  phone?: string;
  messageId?: string | number;
  onSelectTemplate?: (activityTypeId: number) => void;
  registerExternalController?: (fn: {
    setHideActivityTypeSelector: (hidden: boolean) => void;
    setPhone: (phone: string | undefined) => void;
    setPhoneLoading: (loading: boolean) => void;
    setCanAssignCollector: (can: boolean) => void;
    reloadCollector: () => void;
    setMessageId: (messageId: string | number | undefined) => void;
  }) => void;
}) => {
  const [currentMessageId, setCurrentMessageId] = useState<
    string | number | undefined
  >(messageId);
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const eyeBtnRef = useRef<HTMLButtonElement | null>(null);
  const [hideActivityTypeSelector, setHideActivityTypeSelector] =
    useState(false);
  const [currentPhone, setCurrentPhone] = useState<string | undefined>(phone);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [collector, setCollector] = useState<
    { userId?: number; username?: string } | undefined
  >();
  const [canChangeCollector, setCanChangeCollector] = useState<boolean>(false);
  const [collectorLoading, setCollectorLoading] = useState<boolean>(false);
  const [viewHistory, setViewHistory] = useState<WAViewUser[] | null>(null);
  const [viewHistoryLoading, setViewHistoryLoading] = useState(false);
  const [viewHistoryOpen, setViewHistoryOpen] = useState(false);

  const loadCollector = useCallback(async () => {
    setCollectorLoading(true);
    const ret = await ActivityService.getCollector(currentMessageId);
    setCollector(ret);
    setCollectorLoading(false);
  }, [currentMessageId]);

  useEffect(() => {
    registerExternalController?.({
      setHideActivityTypeSelector: (hidden: boolean) =>
        setHideActivityTypeSelector(hidden),
      setPhone: (phone: string | undefined) => setCurrentPhone(phone),
      setPhoneLoading: (loading: boolean) => setPhoneLoading(loading),
      setCanAssignCollector: (can: boolean) => setCanChangeCollector(can),
      reloadCollector: () => loadCollector(),
      setMessageId: (id: string | number | undefined) => setCurrentMessageId(id)
    });
  }, [registerExternalController, loadCollector]);

  // Actualizar el teléfono cuando cambia el prop inicial
  useEffect(() => {
    setCurrentPhone(phone);
  }, [phone]);

  useEffect(() => {
    setCurrentMessageId(messageId);
  }, [messageId]);

  // Si cambia el mensaje, el historial de vistas cargado ya no corresponde
  useEffect(() => {
    setViewHistory(null);
  }, [currentMessageId]);

  useEffect(() => {
    loadCollector();
  }, [loadCollector]);

  const handleEyeClick = async () => {
    setViewHistoryOpen((o) => !o);
    if (viewHistory === null && currentMessageId) {
      setViewHistoryLoading(true);
      const ret = await ActivityService.getWAViewHistory(currentMessageId);
      setViewHistory(ret instanceof Error ? [] : ret);
      setViewHistoryLoading(false);
    }
  };

  const setCollectorHandle = async () => {
    setCollectorLoading(true);
    const ret = await ActivityService.setCollector(currentMessageId);
    setCollector(ret);
    setCollectorLoading(false);
  };

  const activityTypeOptions = CompanyService.getActivityTypes()
    .filter((x) => x.SMS)
    .map((x) => ({ value: x.ActivityTypeID, text: x.ActivityTypeName }));

  const templates = activityTypeOptions.map((el) => ({
    id: el.value as number,
    text: el.text
  }));

  return (
    <div className="wa-title-wrap" style={{ width: "100%" }}>
      <div className="wa-title">
        <div className="wa-title-name" title={name ?? "—"}>
          {name ?? "—"}
        </div>
        <div className="wa-title-phone" title={currentPhone ?? "—"}>
          {phoneLoading ? (
            <i
              className="fas fa-spinner-third fa-spin"
              style={{ marginRight: "8px" }}
            ></i>
          ) : (
            (currentPhone ?? "—")
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        {!hideActivityTypeSelector ? (
          <div className="wa-title-actions ms-2">
            <button
              ref={btnRef}
              className="wa-title-dropdown-btn"
              onClick={() => setOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={open}
              title="Seleccionar plantilla"
            >
              <i className="fa-solid fa-list"></i>
              <i className="fa-solid fa-chevron-down ms-1"></i>
            </button>

            <PortalDropdown
              anchorRef={btnRef as any}
              open={open}
              onClose={() => setOpen(false)}
              minWidth={200}
            >
              <div className="wa-title-dropdown-menu">
                <div
                  key="none"
                  className="dropdown-item text-secondary"
                  onClick={() => {
                    onSelectTemplate?.(NaN as any);
                    setOpen(false);
                  }}
                >
                  {TranslationService.translate.None ?? "Ninguno"}
                </div>
                {templates.map((t) => (
                  <div
                    key={t.id}
                    className="dropdown-item"
                    onClick={() => {
                      onSelectTemplate?.(t.id);
                      setOpen(false);
                    }}
                  >
                    {t.text}
                  </div>
                ))}
              </div>
            </PortalDropdown>
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginLeft: "auto"
          }}
        >
          <div>
            {!collectorLoading ? (
              <i className="fas fa-user-tie me-1"></i>
            ) : (
              <i
                className="fas fa-spinner-third fa-spin"
                style={{ marginRight: "8px" }}
              ></i>
            )}
            {!collectorLoading && collector?.userId && (
              <span>{collector.username}</span>
            )}
            {!collectorLoading &&
              !collector?.userId &&
              canChangeCollector &&
              currentMessageId && (
                <button
                  className="wa-title-dropdown-btn"
                  onClick={setCollectorHandle}
                >
                  {TranslationService.translate.Assign}
                </button>
              )}
          </div>

          {currentMessageId && CompanyService.getIsAdmin() && (
            <>
              <button
                ref={eyeBtnRef}
                className="wa-title-dropdown-btn"
                onClick={handleEyeClick}
                title={TranslationService.translate.SeenOn}
                disabled={viewHistoryLoading}
                aria-haspopup="menu"
                aria-expanded={viewHistoryOpen}
              >
                {viewHistoryLoading ? (
                  <i className="fas fa-spinner-third fa-spin"></i>
                ) : (
                  <i className="fas fa-eye"></i>
                )}
              </button>

              <PortalDropdown
                anchorRef={eyeBtnRef as any}
                open={viewHistoryOpen}
                onClose={() => setViewHistoryOpen(false)}
                minWidth={260}
              >
                <div className="wa-view-history-menu">
                  {viewHistoryLoading && (
                    <div className="wa-view-history-loading">
                      <i className="fas fa-spinner-third fa-spin"></i>
                    </div>
                  )}
                  {!viewHistoryLoading &&
                    viewHistory?.map((item) => {
                      const initials = item.UserFullname.split(" ")
                        .slice(0, 2)
                        .map((w) => w[0])
                        .join("")
                        .toUpperCase();
                      const date = formatDateTimeDigits(
                        restTimezone(new Date(item.Date))
                      );
                      return (
                        <div key={item.UserId} className="wa-view-history-item">
                          <span
                            className="wa-view-history-avatar"
                            style={{ background: "#d7d7d7", color: "#1f2b45" }}
                          >
                            {initials}
                          </span>
                          <span className="wa-view-history-info">
                            <span className="wa-view-history-name">
                              {item.UserFullname}
                            </span>
                            <span className="wa-view-history-date">
                              {TranslationService.translate.SeenOn} {date}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                </div>
              </PortalDropdown>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
